import { useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../api';
import { Button, Card } from '../components/ui';
import { useT } from '../i18n';

/**
 * La guía de arranque de una cuenta nueva.
 *
 * ── Por qué existe una pantalla y no un párrafo en el README ──────────────────
 * Porque una cuenta nueva abre el panel y **no ve nada**: sus influencers son suyas y todavía no
 * tiene ninguna, así que el tablero está vacío, la galería está vacía y no hay nada que explique
 * si eso es lo normal o si algo falló. Un panel vacío sin contexto se lee como roto.
 *
 * Y porque el paso que sigue no es obvio: hay que crear una cuenta de Cloudflare propia y pegar
 * su token acá. Nadie adivina eso mirando un tablero en cero.
 *
 * Se deja de mostrar cuando la persona pulsa que terminó, no cuando el sistema decide que ya
 * está lista. Quien quiera leerla de nuevo la tiene en el menú.
 */
export function Bienvenida({ onDone }: { onDone?: () => void }) {
  const { t } = useT();
  const [listo, setListo] = useState(false);

  return (
    <div className="mx-auto max-w-3xl space-y-6">
      <div>
        <h1 className="text-3xl font-semibold tracking-tight">{t('bien.titulo')}</h1>
        <p className="mt-1 text-sm text-[var(--color-ink-muted)]">{t('bien.bajada')}</p>
      </div>

      <Card title={t('bien.queEs')}>
        <p className="max-w-2xl text-sm">
          {t('bien.queEsAntes')}
          <strong>{t('bien.crearYOperar')}</strong>
          {t('bien.queEsDespues')}
        </p>
        <p className="mt-3 max-w-2xl text-sm text-[var(--color-ink-muted)]">
          {t('bien.loDificilAntes')}
          <strong>{t('bien.mismaPersona')}</strong>
          {t('bien.loDificilMedio')}
          <em>{t('bien.conEsasImagenes')}</em>
          {t('bien.loDificilFin')}
        </p>
      </Card>

      {/*
        El paso que nadie adivina. Va primero porque sin esto no se puede generar nada, y
        porque es el único que ocurre fuera de este panel.
      */}
      <Card title={t('bien.paso1')}>
        <p className="max-w-2xl text-sm text-[var(--color-ink-muted)]">
          {t('bien.paso1QueAntes')}
          <strong>{t('bien.creditosPorDia')}</strong>
          {t('bien.paso1QueDespues')}
        </p>

        <ol className="mt-4 max-w-2xl space-y-3 text-sm">
          <li className="flex gap-3">
            <span className="font-mono text-xs text-[var(--color-ink-muted)]">01</span>
            <span>
              {t('bien.creaCuentaEn')}
              <a
                href="https://dash.cloudflare.com/sign-up"
                target="_blank"
                rel="noreferrer noopener"
                className="underline underline-offset-2 hover:text-[var(--color-accent)]"
              >
                dash.cloudflare.com/sign-up ↗
              </a>
              {t('bien.noPideTarjeta')}
            </span>
          </li>
          <li className="flex gap-3">
            <span className="font-mono text-xs text-[var(--color-ink-muted)]">02</span>
            <span>
              {t('bien.entraA')}
              <a
                href="https://dash.cloudflare.com/profile/api-tokens"
                target="_blank"
                rel="noreferrer noopener"
                className="underline underline-offset-2 hover:text-[var(--color-accent)]"
              >
                {t('bien.perfilTokens')}
              </a>{' '}
              {t('bien.yCreaUnoAntes')}
              <strong>Workers AI</strong>
              {t('bien.yCreaUnoDespues')}
            </span>
          </li>
          <li className="flex gap-3">
            <span className="font-mono text-xs text-[var(--color-ink-muted)]">03</span>
            <span>
              {t('bien.copiaTambienAntes')}
              <strong>{t('bien.idDeCuenta')}</strong>
              {t('bien.copiaTambienDespues')}
            </span>
          </li>
          <li className="flex gap-3">
            <span className="font-mono text-xs text-[var(--color-ink-muted)]">04</span>
            <span>
              {t('bien.pegaLosDos')}
              <Link
                to="/credenciales"
                className="underline underline-offset-2 hover:text-[var(--color-accent)]"
              >
                {t('bien.credencialesCloudflare')}
              </Link>
              .
            </span>
          </li>
        </ol>
      </Card>

      <Card title={t('bien.paso2')}>
        <p className="max-w-2xl text-sm text-[var(--color-ink-muted)]">
          {t('bien.paso2QueAntes')}
          <strong>Google Gemini</strong>
          {t('bien.oGroq')}
          <strong>Groq</strong>
          {t('bien.paso2QueDespues')}
        </p>
        <p className="mt-3 max-w-2xl text-sm text-[var(--color-ink-muted)]">
          {t('bien.seSacaEn')}
          <a
            href="https://aistudio.google.com/apikey"
            target="_blank"
            rel="noreferrer noopener"
            className="underline underline-offset-2 hover:text-[var(--color-accent)]"
          >
            aistudio.google.com/apikey ↗
          </a>{' '}
          {t('bien.ySePegaEn')}
          <Link
            to="/credenciales"
            className="underline underline-offset-2 hover:text-[var(--color-accent)]"
          >
            {t('cred.titulo')}
          </Link>
          {t('bien.sinEstoIgual')}
        </p>
      </Card>

      <Card title={t('bien.paso3')}>
        <p className="max-w-2xl text-sm text-[var(--color-ink-muted)]">
          {t('bien.paso3QueAntes')}
          <strong>{t('bien.influencersNueva')}</strong>
          {t('bien.paso3QueDespues')}
        </p>
        <p className="mt-3 max-w-2xl text-xs text-[var(--color-ink-muted)]">
          {t('bien.conLoGratuitoAntes')}
          <strong>fal</strong>
          {t('bien.conLoGratuitoDespues')}
        </p>
      </Card>

      {/*
        Lo que conviene saber antes de gastar, no después. Va acá y no escondido en ajustes
        porque el primer día es cuando alguien deja algo generando y se olvida.
      */}
      <Card title={t('bien.antesDeGastar')}>
        <ul className="max-w-2xl list-disc space-y-2 pl-5 text-sm text-[var(--color-ink-muted)]">
          <li>
            {t('bien.modoCeroAntes')}
            <strong>{t('bien.modoCero')}</strong>
            {t('bien.modoCeroDespues')}
          </li>
          <li>
            {t('bien.siLoDesactivasAntes')}
            <strong>{t('bien.topeDiarioYMensual')}</strong>
            {t('bien.siLoDesactivasDespues')}
          </li>
          <li>
            <strong>{t('bien.videoCuestaMas')}</strong>
            {t('bien.videoCuestaMasQue')}
          </li>
        </ul>
      </Card>

      {/*
        El manual va al final y no arriba: quien abre esta pantalla por primera vez necesita
        arrancar, no leer. Al que vuelve buscando un detalle, en cambio, esto es lo que busca.

        En pestaña nueva a propósito — se consulta con el panel al lado.
      */}
      <Card title={t('bien.manualCompleto')}>
        <p className="max-w-2xl text-sm text-[var(--color-ink-muted)]">
          {t('bien.manualCompletoQue')}
        </p>
        <a
          href="/manual"
          target="_blank"
          rel="noreferrer noopener"
          className="mt-4 inline-block text-sm underline underline-offset-2 hover:text-[var(--color-accent)]"
        >
          {t('bien.abrirManual')}
        </a>
      </Card>

      <div className="flex flex-wrap items-center gap-3 pb-4">
        <Button
          disabled={listo}
          onClick={() => {
            setListo(true);
            void api.post('/api/users/me/onboarded', {}).then(() => onDone?.());
          }}
        >
          {t(listo ? 'bien.listo' : 'bien.entendido')}
        </Button>
        <span className="text-xs text-[var(--color-ink-muted)]">
          {t('bien.quedaEnElMenu')}
        </span>
      </div>
    </div>
  );
}
